import { Suspense, useEffect, useState } from "react";
import { prefetchAppPages } from "@/lib/pages";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { SetupGuide } from "@/components/SetupGuide";
import { CustomerCardHost } from "@/components/customer/CustomerCard";
import { ContactCardHost } from "@/components/contact/ContactCard";
import { AssistantWidget } from "@/components/assistant/AssistantWidget";
import {
  LayoutDashboard,
  FileText,
  Users,
  UsersRound,
  UserPlus,
  PieChart,
  Archive,
  Upload,
  ScanText,
  LogOut,
  Menu,
  X,
  UserCog,
  Wallet, Settings2, HandCoins, ShieldCheck, MessageCircle, Briefcase, PlusCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { autoOpenOnce } from "@/lib/whatsapp";

const NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, end: true },
  { group: "Portföy", icon: Briefcase, items: [
    { to: "/policies", label: "Poliçeler", icon: FileText },
    { to: "/customers", label: "Müşteriler", icon: Users },
    { to: "/gruplar", label: "Müşteri Grupları", icon: UsersRound },
    { to: "/prospects", label: "Potansiyel Müşteriler", icon: UserPlus },
    { to: "/archive", label: "Arşiv", icon: Archive },
  ] },
  { group: "Ekle", icon: PlusCircle, items: [
    { to: "/data-entry", label: "Veri Girişi", icon: Upload },
    { to: "/proforma", label: "Proforma Girişi", icon: ScanText },
  ] },
  { group: "Para", icon: Wallet, items: [
    { to: "/muhasebe", label: "Açık Hesaplar", icon: Wallet },
    { to: "/hakedis", label: "Hakediş", icon: HandCoins },
    { to: "/reports", label: "Raporlar", icon: PieChart },
  ] },
  { group: "Ayarlar", icon: Settings2, items: [
    { to: "/settings", label: "Ayarlar", icon: Settings2 },
    { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle },
  ] },
];

const linkCls = (sub) => ({ isActive }) =>
  `relative flex items-center gap-3 ${sub ? "pl-9 pr-3 py-2 text-[13px]" : "px-3 py-2.5 text-sm"} rounded-md font-medium transition-colors duration-200 ${
    isActive
      ? "bg-white/10 text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-amber-400"
      : "text-emerald-100/70 hover:bg-white/5 hover:text-white"
  }`;

const NavItem = ({ to, label, icon: Icon, end, sub, onClick }) => (
  <NavLink to={to} end={end} onClick={onClick} data-testid={`nav-${label.toLowerCase()}`} className={linkCls(sub)}>
    <Icon className={sub ? "h-4 w-4" : "h-[18px] w-[18px]"} strokeWidth={1.5} />
    {label}
  </NavLink>
);

const NavGroup = ({ group, icon: Icon, items, onNavigate }) => {
  const { pathname } = useLocation();
  const hasActive = items.some((i) => pathname.startsWith(i.to));
  const storeKey = `nav-open-${group}`;
  const [open, setOpen] = useState(() => localStorage.getItem(storeKey) !== "0");
  const toggle = () => { localStorage.setItem(storeKey, open ? "0" : "1"); setOpen(!open); };
  const shown = open || hasActive;
  return (
    <div>
      <button onClick={toggle} data-testid={`nav-group-${group.toLowerCase()}`}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-200/50 hover:text-emerald-100 transition-colors">
        <Icon className="h-4 w-4" strokeWidth={1.5} />
        <span className="flex-1 text-left">{group}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${shown ? "" : "-rotate-90"}`} />
      </button>
      {shown && <div className="space-y-0.5 mt-0.5">{items.map((i) => <NavItem key={i.to} {...i} sub onClick={onNavigate} />)}</div>}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => { prefetchAppPages(); }, []);
  useEffect(() => {
    const h = () => autoOpenOnce();
    document.addEventListener("click", h, { once: true });
    return () => document.removeEventListener("click", h);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full w-full min-w-0">
      <div className="flex items-center gap-2.5 px-6 h-16 border-b border-white/10">
        <img src="/logo192.png?v=6" alt="Ai Sigortan" className="h-8 w-8 object-contain" data-testid="brand-logo" />
        <div className="leading-tight">
          <p className="font-heading font-bold text-[15px] tracking-tight text-amber-400" data-testid="brand-name">
            Ai Sigortan
          </p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-200/50">
            Sigorta Yönetimi
          </p>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-3 overflow-y-auto">
        {(user?.role === "admin"
          ? NAV.map((n) => (n.group === "Ayarlar" ? { ...n, items: [...n.items, { to: "/admin", label: "Yönetici", icon: UserCog }] } : n))
          : NAV
        ).map((n) =>
          n.group
            ? <NavGroup key={n.group} {...n} onNavigate={() => setOpen(false)} />
            : <NavItem key={n.to} {...n} onClick={() => setOpen(false)} />
        )}
      </nav>

      <div className="p-3 border-t border-white/10">
        <NavLink to="/profile" data-testid="profile-link" title="Profilim"
          className="flex items-center gap-3 px-3 py-2 mb-1 rounded-md hover:bg-white/5 transition-colors">
          <UserAvatar user={user} />
          <div className="leading-tight overflow-hidden">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-emerald-200/50 truncate">{user?.email}</p>
          </div>
        </NavLink>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start gap-3 text-emerald-100/70 hover:text-red-300 hover:bg-white/5"
        >
          <LogOut className="h-[18px] w-[18px]" strokeWidth={1.5} />
          Çıkış Yap
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#e9ebee] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#062a20] fixed inset-y-0 left-0 z-40 shadow-xl">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#062a20]">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-6 lg:px-8 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
          <button
            className="lg:hidden text-zinc-600"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Menü"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex-1" />
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 mr-2" data-testid="secure-badge">
            <ShieldCheck className="h-3.5 w-3.5" /> Güvenli bağlantı
          </span>
          <span className="text-xs text-zinc-500 tabular">
            {new Date().toLocaleDateString("tr-TR", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1600px] mx-auto">
          <SetupGuide />
          <Suspense fallback={<div className="py-20 text-center text-sm text-zinc-400" data-testid="page-loading">Yükleniyor…</div>}>
            <Outlet />
          </Suspense>
          <CustomerCardHost />
          <ContactCardHost />
          <AssistantWidget />
        </main>
      </div>
    </div>
  );
}
